import { useCallback, useEffect, useRef, useState } from 'react';
import useIsMounted from '../../shared/hooks/useIsMounted';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { API_URL } from '../../config/env';

const POLL_MS = 5000;

/** Aborting an in-flight poll is routine teardown, not a failure to surface. */
function isAbortError(err) {
  return err?.name === 'AbortError';
}

export default function useKnowledgeIndexing({ poll = true, selectedMediaId = null } = {}) {
  const isMounted = useIsMounted();
  const [active, setActive] = useState([]);
  const [recent, setRecent] = useState([]);
  const [workerHealth, setWorkerHealth] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [actionResult, setActionResult] = useState(null);
  const pollRef = useRef(null);
  // One controller for the hook's lifetime: unmounting cancels every request it started,
  // so a slow or wedged backend cannot leave responses in flight behind the page.
  const abortRef = useRef(null);
  // A tick that is still waiting on the backend must not start another one. Without this
  // guard a backend slower than POLL_MS accumulates one unfinished request every 5s.
  const tickInFlightRef = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;
    return () => controller.abort();
  }, []);

  const fetchIndex = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(`${API_URL}/v1/admin/knowledge_indexing`, {
        signal: abortRef.current?.signal,
      });
      if (!res.ok) throw new Error('Failed to load AI indexing status');
      const data = await res.json();
      setActive(data.active || []);
      setRecent(data.recent || []);
      setWorkerHealth(data.worker_health || null);
    } catch (err) {
      if (!isMounted.current || isAbortError(err)) return;
      setError(err.message || 'Failed to load');
    } finally {
      if (!isMounted.current) return;
      if (!silent) setLoading(false);
    }
  }, [isMounted]);

  const fetchDetail = useCallback(async (mediaId, silent = false) => {
    if (!mediaId) {
      setDetail(null);
      return;
    }
    if (!silent) setDetailLoading(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/v1/admin/knowledge_indexing/${mediaId}`, {
        signal: abortRef.current?.signal,
      });
      if (!res.ok) throw new Error('Failed to load indexing logs');
      setDetail(await res.json());
    } catch (err) {
      if (!isMounted.current || isAbortError(err)) return;
      setError(err.message || 'Failed to load logs');
      setDetail(null);
    } finally {
      if (!isMounted.current) return;
      if (!silent) setDetailLoading(false);
    }
  }, [isMounted]);

  useEffect(() => {
    fetchIndex();
  }, [fetchIndex]);

  useEffect(() => {
    fetchDetail(selectedMediaId, false);
  }, [selectedMediaId, fetchDetail]);

  // Background refresh. A hidden tab is skipped — an admin cannot read the panel then, and
  // polling on anyway cost ~720 requests an hour per open tab. Returning to the tab refreshes
  // immediately rather than waiting out the interval.
  useEffect(() => {
    if (!poll) return undefined;

    const tick = async () => {
      if (document.hidden) return;
      if (tickInFlightRef.current) return;
      tickInFlightRef.current = true;
      try {
        await fetchIndex(true);
        if (selectedMediaId) await fetchDetail(selectedMediaId, true);
      } finally {
        tickInFlightRef.current = false;
      }
    };

    const onVisibilityChange = () => {
      if (!document.hidden) tick();
    };

    pollRef.current = setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(pollRef.current);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [poll, selectedMediaId, fetchIndex, fetchDetail]);

  const retryFailed = useCallback(async () => {
    setActionLoading('retry_failed');
    setError(null);
    try {
      const res = await fetchWithAuth(`${API_URL}/v1/admin/knowledge_indexing/retry_failed`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Retry failed');
      setActionResult({ type: 'retry_failed', ...data });
      await fetchIndex(true);
      if (selectedMediaId) await fetchDetail(selectedMediaId, true);
    } catch (err) {
      if (!isMounted.current) return;
      setError(err.message || 'Retry failed');
    } finally {
      if (!isMounted.current) return;
      setActionLoading(null);
    }
  }, [fetchIndex, fetchDetail, selectedMediaId, isMounted]);

  // Re-queues one document. `ocr` is persisted on the document by the API, so the choice
  // sticks for later retries rather than applying to this run only.
  const reindex = useCallback(async (mediaId, { ocr } = {}) => {
    setActionLoading(`reindex_${mediaId}`);
    setError(null);
    try {
      const res = await fetchWithAuth(`${API_URL}/v1/admin/knowledge_indexing/${mediaId}/reindex`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(ocr === undefined ? {} : { ocr }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.errors?.join(', ') || data.error || 'Re-index failed');
      setActionResult({ type: 'reindex', filename: data.filename, ocr: data.knowledge_index_ocr });
      await fetchIndex(true);
      if (selectedMediaId) await fetchDetail(selectedMediaId, true);
    } catch (err) {
      if (!isMounted.current) return;
      setError(err.message || 'Re-index failed');
    } finally {
      if (!isMounted.current) return;
      setActionLoading(null);
    }
  }, [fetchIndex, fetchDetail, selectedMediaId, isMounted]);

  const reconcileDocling = useCallback(async () => {
    setActionLoading('reconcile_docling');
    setError(null);
    try {
      const res = await fetchWithAuth(`${API_URL}/v1/admin/knowledge_indexing/reconcile_docling`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Docling check failed');
      setActionResult({ type: 'reconcile_docling', ...data });
      await fetchIndex(true);
      if (selectedMediaId) await fetchDetail(selectedMediaId, true);
    } catch (err) {
      if (!isMounted.current) return;
      setError(err.message || 'Docling check failed');
    } finally {
      if (!isMounted.current) return;
      setActionLoading(null);
    }
  }, [fetchIndex, fetchDetail, selectedMediaId, isMounted]);

  return {
    active,
    recent,
    workerHealth,
    detail,
    loading,
    detailLoading,
    error,
    actionLoading,
    actionResult,
    clearActionResult: () => setActionResult(null),
    refresh: fetchIndex,
    retryFailed,
    reconcileDocling,
    reindex,
  };
}
